import React from 'react';
import './ReviewCard.css';

interface Workflow {
  workflowId: string;
  storeId: string;
  platform: string;
  rating: number;
  reviewText: string;
  reviewerName: string;
  reviewTimestamp: string;
  tier: number;
  status: string;
  complaints: string[];
  posContext: any;
  slaDeadline: string;
  createdAt: string;
}

interface Props {
  workflow: Workflow;
  onClick: (workflowId: string) => void;
}

const TIER_COLORS: { [key: number]: string } = { 1: '#3b82f6', 2: '#f59e0b', 3: '#ef4444', 4: '#7c3aed' };
const TIER_NAMES: { [key: number]: string } = { 1: 'Minor', 2: 'Moderate', 3: 'Severe', 4: 'Safety' };

const STATUS_CONFIG: { [key: string]: { label: string; color: string } } = {
  AI_READY: { label: 'AI Ready', color: '#3b82f6' },
  AUTO_APPROVED: { label: 'Auto-Approved', color: '#8b5cf6' },
  APPROVED: { label: 'Sent', color: '#22c55e' },
  REPLY_ONLY: { label: 'Reply Only', color: '#6b7280' },
  OVERDUE: { label: 'Overdue', color: '#ef4444' },
  MISSED: { label: 'Missed', color: '#991b1b' },
  ESCALATED: { label: 'Escalated', color: '#dc2626' },
  CLOSED: { label: 'Closed', color: '#6b7280' }
};

const PLATFORM_ICONS: { [key: string]: string } = {
  google: 'fab fa-google',
  yelp: 'fab fa-yelp',
  micro_survey: 'fas fa-clipboard-list',
  unknown: 'fas fa-comment'
};

export const ReviewCard: React.FC<Props> = ({ workflow, onClick }) => {
  const statusConfig = STATUS_CONFIG[workflow.status] || { label: workflow.status, color: '#6b7280' };
  const isOverdue = new Date(workflow.slaDeadline) < new Date() && ['AI_READY', 'AUTO_APPROVED'].includes(workflow.status);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < rating ? 'filled' : 'empty'}`}>
        <i className={`fas fa-star`}></i>
      </span>
    ));
  };

  const formatTimeAgo = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return `${Math.floor(diff / (1000 * 60))}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const formatSlaRemaining = () => {
    const diff = new Date(workflow.slaDeadline).getTime() - Date.now();
    if (diff <= 0) return 'Overdue';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  };

  return (
    <div className={`review-card ${isOverdue ? 'overdue' : ''}`} onClick={() => onClick(workflow.workflowId)}>
      <div className="card-top">
        <div className="card-badges">
          <span className="platform-badge">
            <i className={PLATFORM_ICONS[workflow.platform] || PLATFORM_ICONS.unknown}></i>
            {workflow.platform === 'micro_survey' ? 'Survey' : workflow.platform}
          </span>
          <span className="tier-badge" style={{ backgroundColor: TIER_COLORS[workflow.tier] }}>
            T{workflow.tier} {TIER_NAMES[workflow.tier]}
          </span>
          <span className="status-badge" style={{ backgroundColor: statusConfig.color }}>
            {statusConfig.label}
          </span>
        </div>
        <div className="card-meta">
          <span className="sla-timer" style={{ color: isOverdue ? '#ef4444' : '#666' }}>
            <i className="fas fa-clock"></i> {formatSlaRemaining()}
          </span>
        </div>
      </div>

      <div className="card-rating">
        <div className="stars">{renderStars(workflow.rating)}</div>
        <span className="reviewer-name">{workflow.reviewerName || 'Anonymous'}</span>
        <span className="review-time">{formatTimeAgo(workflow.reviewTimestamp || workflow.createdAt)}</span>
      </div>

      <div className="card-text">
        {workflow.reviewText
          ? (workflow.reviewText.length > 200 ? workflow.reviewText.substring(0, 200) + '...' : workflow.reviewText)
          : '(No review text)'}
      </div>

      {workflow.complaints && workflow.complaints.length > 0 && (
        <div className="card-complaints">
          {workflow.complaints.map(c => (
            <span key={c} className="complaint-pill">{c.replace(/_/g, ' ')}</span>
          ))}
        </div>
      )}

      {workflow.posContext && (
        <div className="card-pos">
          <i className="fas fa-receipt"></i>
          POS Match: Order #{workflow.posContext.orderNumber} (${workflow.posContext.orderTotal})
        </div>
      )}
    </div>
  );
};
